import { useCallback, useState } from "react";
import type { InviteStyle, ShareInviteOptions } from "@/features/competitions/share";
import { getShareInviteData } from "@/features/competitions/share";

interface ShareResult {
  shared: boolean;
  method: "web-share" | "clipboard" | "code";
}

export function useShareInvite() {
  const [showSheet, setShowSheet] = useState(false);
  const [style, setStyle] = useState<InviteStyle>("friendly");

  const share = useCallback(
    async (opts: ShareInviteOptions, shareStyle?: InviteStyle): Promise<ShareResult> => {
      const activeStyle = shareStyle ?? style;
      const data = getShareInviteData(
        { ...opts, host: opts.host ?? window.location.origin },
        activeStyle,
      );

      if (navigator.share) {
        try {
          await navigator.share(data);
          return { shared: true, method: "web-share" };
        } catch {
          // user cancelled or API failed
        }
      }

      try {
        await navigator.clipboard.writeText(`${data.text}\n\n${data.url}`);
        return { shared: true, method: "clipboard" };
      } catch {
        return { shared: true, method: "code" };
      }
    },
    [style],
  );

  const copyCode = useCallback(async (code: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(code);
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    share,
    copyCode,
    style,
    setStyle,
    showSheet,
    setShowSheet,
  };
}
